import { useMutation, useQuery } from '@apollo/client';
import { Article } from '@components/InputRichText';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, Stack, Tab, Tabs, TextField, Typography } from '@mui/material';
import { get, isEmpty } from 'lodash';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Note, NoteFormData, NoteTypeFilter, NotesGraphQLOperations, NotesVariableBuilder } from '../types/notes.types';
import { NoteForm } from './NoteForm';
import { NotesList } from './NotesList';

interface EntityNotesManagerProps {
    open: boolean;
    onClose: () => void;
    entityId: string;
    entityName?: string;
    title: string;
    subTitle?: string;
    operations: NotesGraphQLOperations;
    variableBuilder: NotesVariableBuilder;
}

interface EntityNotesContentProps {
    entityId: string;
    entityName?: string;
    title: string;
    subTitle?: string;
    operations: NotesGraphQLOperations;
    variableBuilder: NotesVariableBuilder;
}

export const EntityNotesContent: React.FC<EntityNotesContentProps> = ({
    entityId,
    entityName,
    title,
    subTitle,
    operations,
    variableBuilder,
}) => {
    const { control, handleSubmit, reset } = useForm<NoteFormData>({
        mode: 'all',
        defaultValues: {
            title: '',
            visibility: 'public',
            note: '',
        },
    });

    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<NoteTypeFilter>('all');
    const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
    const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
    const [showNewNoteForm, setShowNewNoteForm] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
    const [mode, setMode] = useState<'view' | 'edit'>('view');

    const listVariables = variableBuilder.list({ entityId });

    const { data, loading } = useQuery(operations.listQuery, {
        variables: listVariables,
        skip: !entityId,
    });

    const [createNote, { loading: createLoading }] = useMutation(operations.createMutation, {
        refetchQueries: [{ query: operations.listQuery, variables: listVariables }],
    });

    const [updateNote, { loading: updateLoading }] = useMutation(operations.updateMutation, {
        refetchQueries: [{ query: operations.listQuery, variables: listVariables }],
    });

    const [deleteNote, { loading: deleteLoading }] = useMutation(operations.deleteMutation, {
        refetchQueries: [{ query: operations.listQuery, variables: listVariables }],
    });

    const isLoading = createLoading || updateLoading || deleteLoading;
    const notes = (get(data, operations.listDataPath) || []) as Note[];

    const filteredNotes = notes.filter((note) => {
        const matchesSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesType = typeFilter === 'all' || note.visibility === typeFilter;
        return matchesSearch && matchesType;
    });

    const submitHandler = async (formData: NoteFormData) => {
        try {
            if (isEmpty(selectedNoteId)) {
                const { data: createData } = await createNote({
                    variables: variableBuilder.create({ entityId, input: formData }),
                });

                const created = get(createData, operations.createDataPath) as Note | null;
                if (created?._id) {
                    setSelectedNoteId(created._id);
                }

                setMode('view');
                setShowNewNoteForm(false);
            } else {
                await updateNote({
                    variables: variableBuilder.update({ entityId, noteId: selectedNoteId, input: formData }),
                });
                setMode('view');
            }
        } catch (err) {
            console.error('Error saving note:', err);
        }
    };

    const deleteItem = async (itemId: string) => {
        if (!itemId) return;
        try {
            await deleteNote({
                variables: variableBuilder.delete({ entityId, noteId: itemId }),
            });
            setSelectedNoteId(null);
            setConfirmDelete(false);
            setDeletingNoteId(null);
            setShowNewNoteForm(false);
        } catch (err) {
            console.error('Error deleting note:', err);
        }
    };

    const handleSelectNote = (note: Note) => {
        setSelectedNoteId(note._id);
        setShowNewNoteForm(false);
        setMode('view');
        reset({
            title: note.title,
            note: note.note,
            visibility: note.visibility,
        });
    };

    const handleDelete = (noteId: string) => {
        setDeletingNoteId(noteId);
        setConfirmDelete(true);
    };

    const handleAddNew = () => {
        setSelectedNoteId(null);
        setShowNewNoteForm(true);
        setMode('edit');
        reset({
            title: '',
            note: '',
            visibility: 'public',
        });
    };

    const handleToggleNoteSelection = (noteId: string) => {
        setSelectedNotes((prev) => (prev.includes(noteId) ? prev.filter((id) => id !== noteId) : [...prev, noteId]));
    };

    const selectedNote = notes.find((note) => note._id === selectedNoteId);

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 'calc(100vw - 48px)',
                    height: '80vh',
                    paddingTop: "16px"
                }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{
                        pb: 2,
                        borderBottom: theme => `1px solid ${theme.palette.border.main}`,
                        flexShrink: 0,
                    }}>
                    <Box sx={{ minWidth: 0, flex: 1, paddingLeft: "16px" }}>
                        <Typography
                            sx={{
                                fontFamily: 'Lemon milk pro',
                                fontSize: '25px',
                                lineHeight: '40px',
                                fontWeight: 500,
                                color: theme => theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
                                marginBottom: '4px',
                            }}>
                            {title}
                        </Typography>
                        {subTitle && (

                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <i className="fi fi-br-eye" style={{ fontSize: 14 }}></i>

                                <Typography
                                    sx={{
                                        fontFamily: 'Plus Jakarta Sans',
                                        fontSize: { xs: '12px', sm: '14px', md: '16px' },
                                        fontWeight: 500,
                                        lineHeight: { xs: '18px', sm: '20px', md: '24px' },
                                    }}>
                                    {subTitle}
                                </Typography>
                            </Stack>
                        )}

                        {entityName && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box
                                    component="i"
                                    className="fi fi-br-eye"
                                    sx={{ fontSize: 14, color: theme => theme.palette.text.secondary }}
                                />

                                <Typography
                                    sx={{
                                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                                        fontSize: '16px',
                                        fontWeight: 500,
                                        color: theme => theme.palette.text.secondary,
                                        lineHeight: '24px',
                                    }}>
                                    {entityName} - Seuls les membres Upercut peuvent voir les commentaires
                                </Typography>
                            </Stack>
                        )}
                    </Box>
                </Stack>

                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        width: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}>
                    <Box
                        sx={{
                            width: '320px',
                            minWidth: 0,
                            borderRight: theme => `1px solid ${theme.palette.border.main}`,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme => theme.palette.background.paper,
                            overflow: 'hidden',
                            paddingLeft: "16px"
                        }}>
                        <Box
                            sx={{
                                pr: 2,
                                pt: 1.5,
                            }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={1}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    autoFocus
                                    placeholder="Rechercher..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Box
                                                    component="i"
                                                    className="fi-br-search"
                                                    sx={{ fontSize: 14, color: theme => theme.palette.text.secondary }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: theme => theme.palette.background.paper,
                                            height: 40,
                                            borderRadius: '16px',
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            padding: '6px 8px',
                                            fontSize: 14,
                                        },
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={handleAddNew}
                                    sx={{
                                        backgroundColor: 'transparent',
                                        color: theme => theme.palette.muted.main,
                                        width: 28,
                                        height: 28,
                                    }}>
                                    <i className="fi-br-note-medical" style={{ fontSize: 16 }} />
                                </IconButton>
                            </Stack>

                            <Stack flexDirection="row" alignItems="center" gap={1} mb={1}>
                                <Tabs
                                    value={typeFilter}
                                    onChange={(_, newValue) => {
                                        setTypeFilter(newValue as NoteTypeFilter);
                                        setSelectedNotes([]);
                                    }}
                                    sx={{
                                        minHeight: 24,
                                        backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                        borderRadius: '8px',
                                        padding: '4px',
                                        '& .MuiTabs-indicator': {
                                            display: 'none',
                                        },
                                        '& .MuiTabs-flexContainer': {
                                            gap: 0,
                                        },
                                    }}>
                                    <Tab value="all" label="Tous" sx={tabSx(typeFilter === 'all')} />
                                    <Tab value="public" label="Public" sx={tabSx(typeFilter === 'public')} />
                                    <Tab value="private" label="Prive" sx={tabSx(typeFilter === 'private')} />
                                </Tabs>
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                flex: 1,
                                overflow: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                            }}>
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                                <NotesList
                                    notes={filteredNotes}
                                    selectedNoteId={selectedNoteId}
                                    selectedNotes={selectedNotes}
                                    onSelectNote={handleSelectNote}
                                    onToggleSelection={handleToggleNoteSelection}
                                    loading={loading}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            backgroundColor: theme => theme.palette.background.default,
                            overflow: 'hidden',
                            p: mode === 'view' ? 2 : 0,
                        }}>
                        {selectedNoteId || showNewNoteForm ? (
                            <>
                                <DialogContent
                                    sx={{
                                        flex: 1,
                                        overflow: 'auto',
                                        borderRadius: mode === 'view' ? '16px 16px 0 0' : undefined,
                                        borderTop: theme => mode === 'view' ? `1px solid ${theme.palette.border.main}` : undefined,
                                        borderLeft: theme => mode === 'view' ? `1px solid ${theme.palette.border.main}` : undefined,
                                        borderRight: theme => mode === 'view' ? `1px solid ${theme.palette.border.main}` : undefined,
                                        backgroundColor: theme => mode === 'view' ? theme.palette.background.paper : 'transparent',
                                    }}>
                                    {mode === 'view' && selectedNoteId ? (
                                        <Box>
                                            <Stack flexDirection="row" alignItems="center" gap={1} mb={2}>
                                                <Typography
                                                    sx={{
                                                        color: theme => theme.palette.text.primary,
                                                        fontSize: 24,
                                                        fontWeight: 700,
                                                        lineHeight: '36px',
                                                        flex: 1,
                                                    }}>
                                                    {selectedNote?.title}
                                                </Typography>
                                            </Stack>
                                            <Article
                                                dangerouslySetInnerHTML={{ __html: selectedNote?.note || '' }}
                                            />
                                        </Box>
                                    ) : (
                                        <NoteForm control={control} />
                                    )}
                                </DialogContent>
                                <DialogActions
                                    sx={{
                                        flexDirection: 'row',
                                        columnGap: 1,
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        backgroundColor: theme => mode === 'view' ? theme.palette.background.paper : 'transparent',
                                        borderRight: theme => mode === 'view' ? `1px solid ${theme.palette.border.main}` : undefined,
                                        borderLeft: theme => mode === 'view' ? `1px solid ${theme.palette.border.main}` : undefined,
                                        borderBottom: theme => mode === 'view' ? `1px solid ${theme.palette.border.main}` : undefined,
                                        borderRadius: mode === 'view' ? '0 0 16px 16px' : undefined,
                                        '& .MuiButton-root': { marginLeft: 0 },
                                    }}>
                                    {mode === 'view' && selectedNoteId ? (
                                        <>
                                            <Typography
                                                sx={{
                                                    fontSize: 14,
                                                    color: theme => theme.palette.text.secondary,
                                                    marginRight: 'auto',
                                                }}>
                                                Derniere mise a jour le{' '}
                                                {new Date(selectedNote?.createdAt || '').toLocaleDateString('fr-FR', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })}
                                            </Typography>
                                            <Button
                                                disabled={isLoading}
                                                variant="outlined"
                                                color="secondary"
                                                startIcon={<i className="fi fi-br-pencil" style={{ fontSize: 14 }} />}
                                                onClick={() => setMode('edit')}
                                                sx={{
                                                    border: '1px solid transparent',
                                                    fontWeight: 700,
                                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                                }}>
                                                Modifier
                                            </Button>
                                            <Button
                                                disabled={isLoading}
                                                color="error"
                                                variant="text"
                                                startIcon={<i className="fi fi-br-trash" style={{ fontSize: 14 }} />}
                                                onClick={() => handleDelete(selectedNoteId)}>
                                                Supprimer
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                disabled={isLoading}
                                                color="secondary"
                                                variant="text"
                                                onClick={() => {
                                                    if (selectedNoteId) {
                                                        setMode('view');
                                                        const note = selectedNote;
                                                        if (note) {
                                                            reset({
                                                                title: note.title,
                                                                note: note.note,
                                                                visibility: note.visibility,
                                                            });
                                                        }
                                                    } else {
                                                        setSelectedNoteId(null);
                                                        setShowNewNoteForm(false);
                                                        reset({
                                                            title: '',
                                                            note: '',
                                                            visibility: 'public',
                                                        });
                                                    }
                                                }}
                                                size="large">
                                                Annuler
                                            </Button>
                                            <Button
                                                disabled={isLoading}
                                                onClick={handleSubmit(submitHandler)}
                                                size="large">
                                                Enregistrer
                                            </Button>
                                        </>
                                    )}
                                </DialogActions>
                            </>
                        ) : (
                            <Box
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <Typography sx={{ color: theme => theme.palette.text.secondary }}>
                                    Selectionnez un commentaire ou creez-en un nouveau
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="sm" fullWidth>
                <DialogContent>
                    <Typography
                        sx={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: theme => theme.palette.text.primary,
                            marginBottom: 2,
                            lineHeight: '36px',
                        }}>
                        Voulez-vous vraiment supprimer ce commentaire ?
                    </Typography>
                    <Typography sx={{ fontSize: 16, color: theme => theme.palette.text.primary }}>
                        Vous etes sur le point d&apos;effacer le commentaire{' '}
                        <Typography component="span" fontWeight={700} sx={{ color: theme => theme.palette.text.primary }}>
                            &quot;{notes?.find((el) => el._id === deletingNoteId)?.title}&quot;
                        </Typography>
                        , vous ne pourrez plus le recuperer a l&apos;avenir.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="text"
                        color="secondary"
                        onClick={() => {
                            setConfirmDelete(false);
                            setDeletingNoteId(null);
                        }}>
                        Annuler
                    </Button>
                    <Button
                        color="error"
                        disabled={deleteLoading}
                        variant="contained"
                        startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        onClick={() => {
                            if (deletingNoteId) {
                                deleteItem(deletingNoteId);
                            }
                        }}>
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const EntityNotesManager: React.FC<EntityNotesManagerProps> = ({
    open,
    onClose,
    entityId,
    entityName,
    title,
    subTitle,
    operations,
    variableBuilder,
}) => {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <EntityNotesContent
                entityId={entityId}
                entityName={entityName}
                subTitle={subTitle}
                title={title}
                operations={operations}
                variableBuilder={variableBuilder}
            />
        </Dialog>
    );
};

const tabSx = (selected: boolean) => ({
    minHeight: 24,
    height: 24,
    minWidth: 'auto',
    px: 1.5,
    py: 0,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: '8px',
    color: selected ? 'text.primary' : 'text.secondary',
    backgroundColor: selected ? 'background.paper' : 'transparent',
    '&.Mui-selected': {
        color: 'text.primary',
        backgroundColor: 'background.paper',
    },
    '&:hover': {
        backgroundColor: selected
            ? 'background.paper'
            : (theme: any) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    },
});
